import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";
import { waLink } from "../lib/api";

const scrollToId = (e, id) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  if (location.pathname.startsWith("/admin")) return null;

  const links = [
    { label: t("nav.inventory"), to: "/auto" },
    { label: t("nav.showroom"), href: "#showroom" },
    { label: t("nav.contact"), href: "#contatti" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-paper/85 backdrop-blur-xl border-b border-ink/5 py-3" : "bg-transparent py-5"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between gap-6">
          <Link to="/" aria-label="LATUAUTONLINE" data-testid="header-logo-link">
            <Logo />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) =>
              l.to ? (
                <Link key={l.label} to={l.to} className="text-sm uppercase tracking-[0.16em] text-smoke hover:text-rosso transition-colors">
                  {l.label}
                </Link>
              ) : (
                <a key={l.label} href={l.href} onClick={(e) => scrollToId(e, l.href.slice(1))} className="text-sm uppercase tracking-[0.16em] text-smoke hover:text-rosso transition-colors">
                  {l.label}
                </a>
              )
            )}
          </nav>

          <div className="flex items-center gap-4 md:gap-6">
            <div className="hidden sm:flex items-center gap-1 font-mono text-xs" data-testid="language-switcher">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-1.5 py-1 transition-colors ${lang === l.code ? "text-rosso underline underline-offset-4" : "text-fog hover:text-ink"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <a
              href={waLink("Buongiorno, vorrei delle informazioni.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp"
              className="hidden md:inline-flex items-center gap-2 bg-blu text-white text-xs uppercase tracking-[0.16em] px-5 py-2.5 hover:bg-rosso transition-colors"
            >
              WhatsApp
            </a>
            <button className="md:hidden p-1" onClick={() => setOpen(true)} aria-label="Menu" data-testid="menu-open-button">
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-blu text-white flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <Logo light />
              <button onClick={() => setOpen(false)} aria-label={t("admin.cancel")} data-testid="menu-close-button">
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {[
                { label: t("nav.inventory"), to: "/auto" },
                { label: t("nav.showroom"), href: "showroom" },
                { label: t("nav.contact"), href: "contatti" },
              ].map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  {l.to ? (
                    <Link to={l.to} className="font-display text-5xl py-3 block hover:text-rosso transition-colors">
                      {l.label}
                    </Link>
                  ) : (
                    <a href={`#${l.href}`} onClick={(e) => { setOpen(false); scrollToId(e, l.href); }} className="font-display text-5xl py-3 block hover:text-rosso transition-colors">
                      {l.label}
                    </a>
                  )}
                </motion.div>
              ))}
              <a
                href={waLink("Buongiorno, vorrei delle informazioni.")}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex w-fit items-center gap-2 border border-white/30 px-6 py-3 text-sm uppercase tracking-[0.18em] hover:bg-rosso hover:border-rosso transition-colors"
              >
                WhatsApp
              </a>
            </nav>
            <div className="px-8 pb-10 flex gap-2 font-mono text-sm">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)} className={`px-2 py-1 ${lang === l.code ? "text-rosso" : "text-white/50"}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
